<script lang="ts">
import type { RegisteredComponent } from '@builder.io/sdk-svelte';
import { Content, isPreviewing } from '@builder.io/sdk-svelte';
import { toNewsCategoryQuery } from '$lib/utils/newsCategoryFilters.js';
import { CUSTOM_COMPONENTS } from '../../../CustomComponents.js';
import { BLOG_NAME } from '../../../config/constants.js';
import { ERoutes } from '../../../config/enums.js';
import CardGridItem from '../../../stories/atoms/CardGridItem/CardGridItem.svelte';
import SectionHeader from '../../../stories/atoms/SectionHeader/SectionHeader.svelte';
import AuthorBio from '../../../stories/molecules/AuthorBio/AuthorBio.svelte';
import BlogPostHeader from '../../../stories/molecules/BlogPostHeader/BlogPostHeader.svelte';
import Breadcrumb from '../../../stories/molecules/Breadcrumb/Breadcrumb.svelte';
import CardGrid from '../../../stories/molecules/CardGrid/CardGrid.svelte';
import Hero from '../../../stories/organisms/Hero/Hero.svelte';

interface BreadcrumbItem {
    text: string;
    to: string;
    active: boolean;
}

// biome-ignore lint/suspicious/noExplicitAny: Data type from +page.server.js
let { data }: { data: any } = $props();

// shortcut to the actual content
let pageContent = $derived(data.builderProps.content?.data || {});

// we want to show unpublished content when in preview mode.
let canShowContent = $derived(pageContent || isPreviewing());

// Extract and validate author information
let authorInfo = $derived(
    pageContent.author?.value?.data?.name
        ? (() => {
              const authorData = pageContent.author.value.data;
              const linkedInUrl = authorData.linkedinUrl;
              let validLinkedIn: string | undefined;

              // Validate LinkedIn URL
              if (linkedInUrl && typeof linkedInUrl === 'string') {
                  try {
                      const url = new URL(linkedInUrl);
                      if (
                          url.protocol === 'https:' &&
                          (url.hostname === 'linkedin.com' || url.hostname === 'www.linkedin.com')
                      ) {
                          validLinkedIn = linkedInUrl;
                      }
                  } catch {
                      // Invalid URL, leave as undefined
                  }
              }

              return {
                  name: authorData.name,
                  role: authorData.role,
                  linkedIn: validLinkedIn,
                  bioImage: authorData.bioImage,
                  shortBio: authorData.shortBio,
              };
          })()
        : undefined,
);

let breadCrumbList: BreadcrumbItem[] = $derived.by(() => {
    const categoryId = pageContent.category?.id ?? pageContent.category?.value?.id;
    const categoryItems =
        (data.allCategories as { id?: string; name?: string }[] | undefined)
            ?.filter((category) => category.id === categoryId)
            .map((category) => ({
                text: category.name ?? '',
                to: `${ERoutes.TheWire}?category=${toNewsCategoryQuery(category.id)}`,
                active: true,
            })) ?? [];

    return [
        {
            text: 'News',
            to: ERoutes.TheWire,
            active: false,
        },
        ...categoryItems,
    ];
});
</script>

{#if canShowContent}
    <!-- force re-rendering of `Content` on client-side navigation -->
    {#key data.builderProps}
        {#if pageContent?.headerImage}
            <Hero
                image={{
                    src: pageContent.headerImage,
                    alt: pageContent.category?.value?.name ?? '',
                }}
                mobileImage={{
                    src: pageContent.headerImageMobile,
                    alt: pageContent.category?.value?.name ?? '',
                }}
                content={BLOG_NAME}
                mastheadTextColour={'#fff'}
            />
        {/if}

        <div class="scroll-content-over-header">
            <Breadcrumb items={breadCrumbList} />

            <!-- Render builder content with all required props -->
            <BlogPostHeader
                title={pageContent.title}
                date={pageContent.date}
                category={pageContent.category}
                author={authorInfo}
                readingTimeMinutes={data.readingTimeMinutes}
            />

            <div class="container builder-content container--blog-post">
                <Content
                    model="blog-article"
                    content={data.builderProps.content}
                    apiKey={data.builderProps.apiKey}
                    customComponents={CUSTOM_COMPONENTS as RegisteredComponent[]}
                />

                {#if authorInfo?.bioImage && authorInfo?.shortBio}
                    <AuthorBio author={authorInfo} authorPostCount={data.authorPostCount} />
                {/if}
            </div>

            {#if pageContent?.relatedPost1?.value?.data?.slug || pageContent?.relatedPost2?.value?.data?.slug}
                <div class="related-container">
                    <SectionHeader titleSize="h3" includeGutter={true}
                        >Similar Reads</SectionHeader
                    >
                    <CardGrid hasBorder={false}>
                        {#snippet children()}
                            {#if pageContent?.relatedPost1?.value?.data?.slug}
                                <CardGridItem
                                    class="builder-block"
                                    to={ERoutes.TheWire +
                                        pageContent.relatedPost1.value?.data
                                            ?.slug}
                                    title={pageContent.relatedPost1.value?.name}
                                    image={{
                                        src: pageContent.relatedPost1.value?.data
                                            ?.thumbnail,
                                        alt: pageContent.relatedPost1.value
                                            ?.name,
                                    }}
                                    key="1"
                                />
                            {/if}
                            {#if pageContent?.relatedPost2?.value?.data?.slug}
                                <CardGridItem
                                    class="builder-block"
                                    to={ERoutes.TheWire +
                                        pageContent.relatedPost2.value?.data
                                            ?.slug}
                                    title={pageContent.relatedPost2.value?.name}
                                    image={{
                                        src: pageContent.relatedPost2.value?.data
                                            ?.thumbnail,
                                        alt: pageContent.relatedPost2.value
                                            ?.name,
                                    }}
                                    key="2"
                                />
                            {/if}
                        {/snippet}
                    </CardGrid>
                </div>
            {/if}
        </div>
    {/key}
{:else}
    Content Not Found
{/if}

<style lang="scss">
    @use '/src/scss/global-constants/mq-config';
    @use 'sass-mq/mq' as *;

    .scroll-content-over-header {
        overflow-x: clip; /* Prevent horizontal scrollbar from full-bleed child elements (100vw) */
        background: var(--colour-white);
        position: relative;
        z-index: 1;
    }

    .container {
        margin: 0 auto;
        max-width: 1300px;
    }

    .related-container {
        margin-top: 5rem;

        @include mq($from: tablet) {
            margin-top: 10rem;
        }

        :global(.section-header) {
            margin-bottom: 3rem;

            @include mq($from: tablet) {
                margin-bottom: 4rem;
            }
        }
    }

</style>
